/**
 * Thematic illustrations for each norm card. All inline SVG, no external assets.
 * Coloring uses the design tokens.
 */

import type { NormEntry } from './catalog';

interface Props {
  kind: NormEntry['illustration'];
  className?: string;
}

export function NormIllustration({ kind, className }: Props) {
  const w = 320;
  const h = 140;
  return (
    <svg
      width="100%"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      aria-hidden
    >
      <defs>
        <pattern id="ill-grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(148,163,184,0.10)" strokeWidth="0.7" />
        </pattern>
        <linearGradient id="amber-fade" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(245,158,11,0.18)" />
          <stop offset="100%" stopColor="rgba(245,158,11,0)" />
        </linearGradient>
      </defs>
      <rect width={w} height={h} fill="url(#ill-grid)" />
      <rect width={w} height={h} fill="url(#amber-fade)" />
      {kind === 'concrete' && <Concrete />}
      {kind === 'seismic' && <Seismic />}
      {kind === 'loads' && <Loads />}
      {kind === 'foundation' && <Foundation />}
      {kind === 'masonry' && <Masonry />}
      {kind === 'aci-book' && <BookACI />}
      {kind === 'pucp' && <BookPUCP />}
      {kind === 'sap' && <SAP />}
    </svg>
  );
}

/* ---- individual illustrations ---- */

function Concrete() {
  // A column section with rebar - most iconic concrete element.
  return (
    <g transform="translate(110, 22) scale(1)">
      <rect
        x="0"
        y="0"
        width="100"
        height="100"
        fill="rgba(148,163,184,0.06)"
        stroke="#475569"
        strokeWidth="2"
      />
      {/* Hatch */}
      <pattern id="concrete-hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="6" stroke="#475569" strokeOpacity="0.18" strokeWidth="1" />
      </pattern>
      <rect x="0" y="0" width="100" height="100" fill="url(#concrete-hatch)" />
      {/* Stirrup */}
      <rect x="10" y="10" width="80" height="80" fill="none" stroke="#0891B2" strokeWidth="1.5" rx="4" />
      {/* Longitudinal bars */}
      {[15, 50, 85].map((x) =>
        [15, 50, 85].map((y) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="3.5" fill="#3B82F6" />
        )),
      )}
      {/* Dimension */}
      <line x1="0" y1="112" x2="100" y2="112" stroke="#94A3B8" strokeWidth="1" />
      <text x="50" y="124" textAnchor="middle" fontSize="9" fontFamily="JetBrains Mono" fill="#94A3B8">
        L = 100 cm
      </text>
    </g>
  );
}

function Seismic() {
  // Building silhouette + sinusoidal seismic wave + arrows.
  return (
    <g>
      {/* Ground */}
      <line x1="0" y1="120" x2="320" y2="120" stroke="#94A3B8" strokeWidth="1" />
      <pattern id="ground-hatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="8" stroke="#475569" strokeOpacity="0.4" strokeWidth="1" />
      </pattern>
      <rect x="0" y="120" width="320" height="20" fill="url(#ground-hatch)" />
      {/* Building */}
      <rect x="120" y="40" width="80" height="80" fill="rgba(148,163,184,0.08)" stroke="#CBD5E1" strokeWidth="1.5" />
      {[50, 65, 80, 95].map((y) => (
        <line key={y} x1="120" y1={y} x2="200" y2={y} stroke="#475569" strokeWidth="0.6" />
      ))}
      {[140, 160, 180].map((x) => (
        <line key={x} x1={x} y1="40" x2={x} y2="120" stroke="#475569" strokeWidth="0.6" />
      ))}
      {/* Lean - small skew */}
      <path d="M 120 40 L 124 36 L 204 36 L 200 40" fill="none" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="3 2" />
      {/* Seismic wave on the ground */}
      <path
        d="M 10 132 Q 25 120 40 132 T 70 132 T 100 132"
        stroke="#EF4444"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M 220 132 Q 235 120 250 132 T 280 132 T 310 132"
        stroke="#EF4444"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
      {/* Force arrow at top of building */}
      <line x1="200" y1="50" x2="240" y2="50" stroke="#F59E0B" strokeWidth="2" />
      <path d="M 240 50 L 234 46 L 234 54 Z" fill="#F59E0B" />
      <text x="220" y="42" textAnchor="middle" fontSize="9" fontFamily="JetBrains Mono" fill="#F59E0B">
        V = ZUCS P / R
      </text>
    </g>
  );
}

function Loads() {
  // Down arrows of varying length labeled with loads.
  return (
    <g>
      {/* Beam */}
      <rect x="60" y="100" width="200" height="14" fill="rgba(148,163,184,0.10)" stroke="#CBD5E1" strokeWidth="1.5" />
      {/* Supports - triangles */}
      <path d="M 60 114 L 50 130 L 70 130 Z" fill="none" stroke="#CBD5E1" strokeWidth="1.2" />
      <path d="M 260 114 L 250 130 L 270 130 Z" fill="none" stroke="#CBD5E1" strokeWidth="1.2" />
      {/* Down arrows */}
      {[80, 110, 140, 170, 200, 230, 260].map((x, i) => {
        const len = 20 + (i % 3) * 8;
        return (
          <g key={x}>
            <line x1={x} y1={40} x2={x} y2={100 - 4} stroke="#3B82F6" strokeWidth="1.6" />
            <path d={`M ${x} ${100 - 4} L ${x - 4} ${100 - 12} L ${x + 4} ${100 - 12} Z`} fill="#3B82F6" />
          </g>
        );
      })}
      <text x="160" y="32" textAnchor="middle" fontSize="10" fontFamily="JetBrains Mono" fill="#3B82F6">
        w = 200 kgf/m2  (vivienda)
      </text>
    </g>
  );
}

function Foundation() {
  // Footing + soil layers + column stub.
  return (
    <g>
      {/* Soil layers */}
      <pattern id="soil1" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="6" stroke="#94A3B8" strokeOpacity="0.4" strokeWidth="1" />
      </pattern>
      <rect x="0" y="60" width="320" height="80" fill="url(#soil1)" />
      <line x1="0" y1="100" x2="320" y2="100" stroke="#475569" strokeWidth="0.8" strokeDasharray="4 4" />
      <text x="6" y="98" fontSize="8" fontFamily="JetBrains Mono" fill="#475569">
        Df
      </text>
      {/* Footing */}
      <rect x="100" y="80" width="120" height="22" fill="rgba(148,163,184,0.10)" stroke="#CBD5E1" strokeWidth="1.5" />
      {/* Column above */}
      <rect x="148" y="20" width="24" height="60" fill="rgba(148,163,184,0.10)" stroke="#CBD5E1" strokeWidth="1.5" />
      {/* Pressure arrows up under footing */}
      {[110, 130, 150, 170, 190, 210].map((x) => (
        <g key={x}>
          <line x1={x} y1={120} x2={x} y2={104} stroke="#22C55E" strokeWidth="1.6" />
          <path d={`M ${x} ${104} L ${x - 3} ${110} L ${x + 3} ${110} Z`} fill="#22C55E" />
        </g>
      ))}
      <text x="160" y="138" textAnchor="middle" fontSize="9" fontFamily="JetBrains Mono" fill="#22C55E">
        q_adm
      </text>
    </g>
  );
}

function Masonry() {
  // Brick wall pattern.
  return (
    <g>
      {Array.from({ length: 6 }, (_, row) =>
        Array.from({ length: 8 }, (_, col) => {
          const offset = row % 2 === 0 ? 0 : 20;
          const x = 60 + col * 32 + offset;
          const y = 30 + row * 14;
          if (x > 280) return null;
          return (
            <rect
              key={`${row}-${col}`}
              x={x}
              y={y}
              width="30"
              height="12"
              fill="rgba(245,158,11,0.18)"
              stroke="#B45309"
              strokeWidth="0.8"
            />
          );
        }),
      )}
    </g>
  );
}

function BookACI() {
  return (
    <g transform="translate(120, 22)">
      <rect x="0" y="0" width="80" height="100" rx="2" fill="#1E293B" stroke="#334155" strokeWidth="1.5" />
      <rect x="2" y="8" width="76" height="84" fill="rgba(245,158,11,0.16)" />
      <text x="40" y="38" textAnchor="middle" fontSize="14" fontFamily="Inter Tight" fontWeight="700" fill="#F59E0B">
        ACI
      </text>
      <text x="40" y="58" textAnchor="middle" fontSize="20" fontFamily="JetBrains Mono" fontWeight="700" fill="#F8FAFC">
        318
      </text>
      <text x="40" y="74" textAnchor="middle" fontSize="9" fontFamily="JetBrains Mono" fill="#CBD5E1">
        2019
      </text>
      <line x1="0" y1="100" x2="80" y2="100" stroke="#F59E0B" strokeWidth="3" />
    </g>
  );
}

function BookPUCP() {
  return (
    <g transform="translate(110, 16)">
      <rect x="0" y="6" width="100" height="110" rx="2" fill="#0F172A" stroke="#334155" strokeWidth="1.5" />
      <rect x="2" y="14" width="96" height="94" fill="rgba(59,130,246,0.10)" />
      <text x="50" y="48" textAnchor="middle" fontSize="11" fontFamily="Inter Tight" fontWeight="700" fill="#3B82F6">
        CONCRETO
      </text>
      <text x="50" y="64" textAnchor="middle" fontSize="11" fontFamily="Inter Tight" fontWeight="700" fill="#3B82F6">
        ARMADO
      </text>
      <line x1="20" y1="74" x2="80" y2="74" stroke="#94A3B8" strokeWidth="0.6" />
      <text x="50" y="88" textAnchor="middle" fontSize="9" fontFamily="JetBrains Mono" fill="#94A3B8">
        PUCP
      </text>
      <line x1="0" y1="116" x2="100" y2="116" stroke="#3B82F6" strokeWidth="3" />
    </g>
  );
}

function SAP() {
  // Schematic of a 2D frame model with nodes and members.
  return (
    <g transform="translate(60, 20)">
      {/* Frame */}
      <rect x="20" y="0" width="160" height="100" fill="none" stroke="#CBD5E1" strokeWidth="1.5" />
      <line x1="20" y1="40" x2="180" y2="40" stroke="#CBD5E1" strokeWidth="1.2" />
      <line x1="20" y1="70" x2="180" y2="70" stroke="#CBD5E1" strokeWidth="1.2" />
      <line x1="100" y1="0" x2="100" y2="100" stroke="#CBD5E1" strokeWidth="1.2" />
      {/* Nodes */}
      {[
        [20, 0],
        [100, 0],
        [180, 0],
        [20, 40],
        [100, 40],
        [180, 40],
        [20, 70],
        [100, 70],
        [180, 70],
        [20, 100],
        [100, 100],
        [180, 100],
      ].map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="3" fill="#F59E0B" stroke="#B45309" strokeWidth="0.8" />
      ))}
      {/* Mode shape - dashed */}
      <path
        d="M 20 100 Q 60 80 100 60 T 180 0"
        stroke="#8B5CF6"
        strokeWidth="1.5"
        strokeDasharray="3 3"
        fill="none"
      />
    </g>
  );
}
